import { useCallback, useEffect, useState } from 'react';
import { FileSpreadsheet, Building2 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import { MultiSelect } from '../../../../components/common-components/multiselect/CommonMultiselect';
import Popup, { PopupFooter } from '../../../../components/common-components/pop-up/Popup';
import { useStore } from '../../../../hooks/useStore';
import type { IHospitalRecordResult, IHospitalResult } from '../../../../models/hospital/HospitalModel';
import { HOSPITAL_RECORD_EXPORT_SORT_MODEL } from '../../../../store/modules/hospital-files/HospitalFilesStore';
import styles from '../HospitalManagement.module.css';

const sanitizeFileNamePart = (value: string) => {
  return value
    .replace(/[ıİ]/g, 'I')
    .replace(/[ğĞ]/g, 'G')
    .replace(/[üÜ]/g, 'U')
    .replace(/[şŞ]/g, 'S')
    .replace(/[öÖ]/g, 'O')
    .replace(/[çÇ]/g, 'C')
    .replace(/[^a-zA-Z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
};

const ExportExcelPopup = () => {
  const { popupStore, hospitalStore, hospitalFilesStore, uiStore } = useStore();
  const [selectedHospitals, setSelectedHospitals] = useState<string[]>([]);
  const [hospitals, setHospitals] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadAllHospitals = useCallback(async () => {
    setLoading(true);
    try {
      const result = await hospitalStore.listAllHospitals();
      if (result && result.rows.length > 0) {
        const hospitalOptions = result.rows
          .filter((hospital: IHospitalResult) => hospital.hospitalName)
          .map((hospital: IHospitalResult) => ({
            value: hospital.id.toString(),
            label: hospital.hospitalName || '',
          }));
        setHospitals(hospitalOptions);
      } else {
        setHospitals([]);
        toast.warning('Hastane bulunamadı');
      }
    } catch (error) {
      console.error('Hastaneler yüklenirken hata:', error);
      toast.error('Hastaneler yüklenirken bir hata oluştu');
      setHospitals([]);
    } finally {
      setLoading(false);
    }
  }, [hospitalStore]);

  useEffect(() => {
    if (popupStore.isExportExcelOpen) {
      loadAllHospitals();
    }
  }, [popupStore.isExportExcelOpen, loadAllHospitals]);

  const handleClose = () => {
    popupStore.setIsExportExcelOpen(false);
    setSelectedHospitals([]);
  };

  const handleExport = async () => {
    if (selectedHospitals.length === 0) {
      toast.error('Lütfen en az bir hastane seçin');
      return;
    }

    setExporting(true);
    uiStore.setLoading(true);

    try {
      const originalHospital = hospitalFilesStore.selectedHospital;
      const allRows: IHospitalRecordResult[] = [];

      for (const hospitalId of selectedHospitals) {
        const hospital = hospitals.find(h => h.value === hospitalId);
        if (!hospital) {
          continue;
        }

        const hospitalResult = await hospitalStore.listAllHospitals();
        const hospitalData = hospitalResult.rows.find(
          (h: IHospitalResult) => h.id.toString() === hospitalId
        );

        if (!hospitalData) {
          continue;
        }

        hospitalFilesStore.setSelectedHospital(hospitalData);

        try {
          const result = await hospitalFilesStore.listHospitalRecord({
            startRow: 0,
            endRow: 1000000,
            filterModel: null,
            sortModel: HOSPITAL_RECORD_EXPORT_SORT_MODEL,
          });

          if (result?.rows.length) {
            allRows.push(...result.rows);
          }
        } catch (error) {
          console.error(`Hastane ${hospital.label} için veri çekme hatası:`, error);
          toast.warning(`${hospital.label} için veri çekilemedi`);
        }
      }

      hospitalFilesStore.setSelectedHospital(originalHospital);

      allRows.sort((a, b) => new Date(b.addDate).getTime() - new Date(a.addDate).getTime());

      const allExportData = allRows.map(item => ({
        ID: item.id,
        Hastane: item.hospitalName,
        'Cihaz Tanımı': item.biomedicalDeviceDescription,
        Marka: item.brand,
        Model: item.model,
        'Seri No': item.serialNumber,
        'Künye No': item.identificationNumber,
        Lokasyon: item.location,
        'Etiket No': item.tagNumberWithColor,
        'Eklenme Tarihi': new Date(item.addDate).toLocaleDateString('tr-TR'),
        'Zimmetli Kişi': item.responsiblePerson,
        Notlar: item.note,
        'Ekleyen Kişi': item.createdBy,
      }));

      if (allExportData.length === 0) {
        toast.error('Seçilen hastaneler için veri bulunamadı');
        return;
      }

      const ws = XLSX.utils.json_to_sheet(allExportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Hastane Cihazları');

      const dateStr = new Date().toISOString().split('T')[0];
      const selectedHospitalNames = selectedHospitals
        .map(id => hospitals.find(h => h.value === id)?.label || '')
        .filter(Boolean)
        .join('_');

      const sanitizedNames = sanitizeFileNamePart(selectedHospitalNames);
      const fileName =
        selectedHospitals.length === 1
          ? `${sanitizedNames}_Hastane_Cihazlari_${dateStr}.xlsx`
          : `Hastane_Cihazlari_${dateStr}.xlsx`;

      XLSX.writeFile(wb, fileName);

      toast.success(
        `${selectedHospitals.length} hastane için ${allExportData.length} kayıt tek bir Excel dosyasına aktarıldı`
      );
      handleClose();
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Excel export sırasında bir hata oluştu');
    } finally {
      setExporting(false);
      uiStore.setLoading(false);
    }
  };

  return (
    <Popup
      isOpen={popupStore.isExportExcelOpen}
      onClose={handleClose}
      title="Excel'e Aktar"
      description="Seçili hastanelerin verilerini Excel'e aktarın"
      variant="info"
      icon={<FileSpreadsheet size={22} strokeWidth={2} />}
      footer={
        <PopupFooter align="end">
          <CommonButton
            type="primary"
            onClick={handleExport}
            disabled={selectedHospitals.length === 0 || loading || exporting}
          >
            {exporting ? 'Aktarılıyor...' : "Excel'e Aktar"}
          </CommonButton>
        </PopupFooter>
      }
    >
      <div className={styles.selectTagsContainer}>
        <div className={styles.dropdownContainer}>
          <MultiSelect
            options={hospitals}
            value={selectedHospitals}
            onChange={setSelectedHospitals}
            label="Hastaneleri Seçin"
            placeholder={loading ? 'Yükleniyor...' : 'Hastane seçin...'}
            disabled={loading}
            startIcon={<Building2 size={18} strokeWidth={2} />}
          />
        </div>
      </div>
    </Popup>
  );
};

export default observer(ExportExcelPopup);
